import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@core/state';
import { SessionActions } from '@core/state/session/session.actions';
import { Store } from '@ngrx/store';
import { AuthService, UsersService } from '@services';
import { Observable, catchError, map, of } from 'rxjs';
import { EUserStatus } from '../enums';
import { IApiResponse } from '../interfaces';
import { User } from '../models';
import { SupervisoreGTMHandler } from '../services/supervisore-gtm-handler.service';

/**
 * AuthGuard
 *
 * @export
 * @class AuthGuard
 * @typedef {AuthGuard}
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  /**
   * Creates an instance of AuthGuard.
   *
   * @constructor
   * @param {Router} router
   * @param {AuthService} authService
   */
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly userService: UsersService,
    private readonly supervisoreGTMHandler: SupervisoreGTMHandler,
    private readonly store: Store<AppState>
  ) {}

  /**
   * Can activate route guard
   *
   * @returns {boolean}
   */
  canActivate(): Observable<boolean> {
    if (this.authService.isLogged()) {
      return this.userService.getMe().pipe(
        map((response: IApiResponse<User>): boolean => {
          this.supervisoreGTMHandler.user.set(response.data);
          this.store.dispatch(SessionActions.setUser({ user: response.data! }));
          if (response.data?.status === EUserStatus.SOSPESO) {
            this.router.navigate(['/user-suspended']);
            return false;
          } else {
            return true;
          }
        }),
        catchError(() => {
          this.router.navigate(['/error']);
          return of(false);
        })
      );
    } else {
      this.router.navigate(['/sign-in']);
      return of(false);
    }
  }
}
